export interface QueryBuilderOption {
  value: string;
  label: string;
}

/**
 * Coerces a partial option to a valid pair for SearchableSelect (requires string
 * `value` and `label` so filter logic can call `toLowerCase()` safely).
 *
 * @param option - Raw option from Firestore or legacy rule data
 * @returns Normalized option, or null when `value` is missing or blank
 */
export function normalizeQueryBuilderOption(
  option: Partial<QueryBuilderOption>,
): QueryBuilderOption | null {
  const value =
    option.value != null && String(option.value).trim() !== ""
      ? String(option.value)
      : "";
  if (!value) return null;
  const label =
    option.label != null && String(option.label).trim() !== ""
      ? String(option.label)
      : value;
  return { value, label };
}

/**
 * Drops invalid options and normalizes value/label strings for select components.
 *
 * @param options - Raw option list
 * @returns Options safe for SearchableSelect filtering
 */
export function normalizeQueryBuilderOptions(
  options: Array<Partial<QueryBuilderOption>>,
): QueryBuilderOption[] {
  return options
    .map(normalizeQueryBuilderOption)
    .filter((o): o is QueryBuilderOption => o != null);
}

export type ValueInputType =
  | "multi-select"
  | "single-select"
  | "text"
  | "number"
  | "date"
  | "none";

/** Returns true when the column type should use a date picker for condition values. */
export function isTimestampDataType(dataType: string | undefined): boolean {
  const dt = (dataType ?? "").toUpperCase();
  return dt === "TIMESTAMP" || dt === "DATE" || dt === "DATETIME";
}

export interface OperatorDefinition {
  value: string;
  label: string;
  valueInputType: ValueInputType;
  valueOptions?: QueryBuilderOption[];
}

export interface FieldDefinition {
  value: string;
  label: string;
  operators: OperatorDefinition[];
  /** When set, the value input component is chosen by data type (STRING → multiselect, INTEGER/FLOAT → number, BOOLEAN → single-select). */
  dataType?: string;
  /** Options for multiselect/single-select when dataType is STRING or value options are from field (e.g. top_values). */
  valueOptions?: QueryBuilderOption[];
}

export interface QueryBuilderConfig {
  fields: FieldDefinition[];
}

export interface QueryCondition {
  type: "condition";
  id: string;
  field: string;
  operator: string;
  value: string | string[];
}

export interface QueryGroup {
  type: "group";
  id: string;
  conjunction: "AND" | "OR";
  children: QueryNode[];
}

export type QueryNode = QueryCondition | QueryGroup;

export type QueryBuilderValue = QueryGroup;

/** Operator catalog entry (value + label) before value-input wiring. */
export type OperatorCatalogEntry = {
  readonly value: string;
  readonly label: string;
};

export const STRING_OPERATORS = [
  { value: "equals", label: "=" },
  { value: "not_equals", label: "!=" },
  { value: "is_any_of", label: "is any of" },
  { value: "is_not_any_of", label: "is not any of" },
  { value: "contains", label: "contains" },
  { value: "does_not_contain", label: "does not contain" },
  { value: "contains_any_of", label: "contains any of" },
  { value: "does_not_contain_any_of", label: "does not contain any of" },
  { value: "starts_with", label: "starts with" },
  { value: "does_not_start_with", label: "does not start with" },
  { value: "ends_with", label: "ends with" },
  { value: "does_not_end_with", label: "does not end with" },
  { value: "is_empty", label: "is empty" },
  { value: "is_not_empty", label: "is not empty" },
] as const satisfies readonly OperatorCatalogEntry[];

export const NUMERIC_OPERATORS = [
  { value: "equals", label: "=" },
  { value: "not_equals", label: "!=" },
  { value: "greater_than", label: ">" },
  { value: "greater_than_or_equal", label: ">=" },
  { value: "less_than", label: "<" },
  { value: "less_than_or_equal", label: "<=" },
  { value: "is_empty", label: "is empty" },
  { value: "is_not_empty", label: "is not empty" },
] as const satisfies readonly OperatorCatalogEntry[];

export const BOOLEAN_OPERATORS = [
  { value: "equals", label: "is" },
  { value: "not_equals", label: "is not" },
] as const satisfies readonly OperatorCatalogEntry[];

export const TIMESTAMP_OPERATORS = [
  { value: "equals", label: "=" },
  { value: "not_equals", label: "!=" },
  { value: "greater_than", label: ">" },
  { value: "greater_than_or_equal", label: ">=" },
  { value: "less_than", label: "<" },
  { value: "less_than_or_equal", label: "<=" },
  { value: "is_any_of", label: "is any of" },
  { value: "is_not_any_of", label: "is not any of" },
  { value: "contains", label: "contains" },
  { value: "does_not_contain", label: "does not contain" },
  { value: "contains_any_of", label: "contains any of" },
  { value: "does_not_contain_any_of", label: "does not contain any of" },
  { value: "starts_with", label: "starts with" },
  { value: "does_not_start_with", label: "does not start with" },
  { value: "ends_with", label: "ends with" },
  { value: "does_not_end_with", label: "does not end with" },
  { value: "is_empty", label: "is empty" },
  { value: "is_not_empty", label: "is not empty" },
] as const satisfies readonly OperatorCatalogEntry[];

export const UNKNOWN_DATATYPE_OPERATORS = [
  { value: "equals", label: "=" },
  { value: "not_equals", label: "!=" },
  { value: "is_any_of", label: "is any of" },
  { value: "is_not_any_of", label: "is not any of" },
  { value: "contains", label: "contains" },
  { value: "does_not_contain", label: "does not contain" },
  { value: "contains_any_of", label: "contains any of" },
  { value: "does_not_contain_any_of", label: "does not contain any of" },
  { value: "starts_with", label: "starts with" },
  { value: "does_not_start_with", label: "does not start with" },
  { value: "ends_with", label: "ends with" },
  { value: "does_not_end_with", label: "does not end with" },
  { value: "is_empty", label: "is empty" },
  { value: "is_not_empty", label: "is not empty" },
] as const satisfies readonly OperatorCatalogEntry[];

const MULTI_VALUE_STRING_OPERATORS = new Set([
  "is_any_of",
  "is_not_any_of",
  "contains_any_of",
  "does_not_contain_any_of",
]);

const EMPTY_VALUE_OPERATORS = new Set(["is_empty", "is_not_empty"]);

const TIMESTAMP_COMPARISON_OPERATORS = new Set([
  "equals",
  "not_equals",
  "greater_than",
  "greater_than_or_equal",
  "less_than",
  "less_than_or_equal",
]);

const BOOLEAN_VALUE_OPTIONS: QueryBuilderOption[] = [
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

/**
 * Maps string-like operator catalog entries to definitions with correct value inputs.
 *
 * @param catalog - Operator value/label catalog for the data type
 * @param valueOptions - Optional field top_values for select-based operators
 * @returns Operator definitions wired for the query builder UI
 */
function mapStringLikeCatalogToDefinitions(
  catalog: readonly OperatorCatalogEntry[],
  valueOptions?: QueryBuilderOption[],
): OperatorDefinition[] {
  const hasOptions = Boolean(valueOptions?.length);

  return catalog.map((op) => {
    if (EMPTY_VALUE_OPERATORS.has(op.value)) {
      return { value: op.value, label: op.label, valueInputType: "none" };
    }
    if (MULTI_VALUE_STRING_OPERATORS.has(op.value)) {
      return {
        value: op.value,
        label: op.label,
        valueInputType: hasOptions ? "multi-select" : "text",
        ...(hasOptions && { valueOptions }),
      };
    }
    if (op.value === "equals" || op.value === "not_equals") {
      return {
        value: op.value,
        label: op.label,
        valueInputType: hasOptions ? "single-select" : "text",
        ...(hasOptions && { valueOptions }),
      };
    }
    return { value: op.value, label: op.label, valueInputType: "text" };
  });
}

/**
 * Maps timestamp-operator catalog entries to definitions with date or text inputs.
 *
 * @param valueOptions - Optional field top_values for multi-select operators
 * @returns Operator definitions for TIMESTAMP / DATE columns
 */
function mapTimestampCatalogToDefinitions(
  valueOptions?: QueryBuilderOption[],
): OperatorDefinition[] {
  const hasOptions = Boolean(valueOptions?.length);

  return TIMESTAMP_OPERATORS.map((op) => {
    if (EMPTY_VALUE_OPERATORS.has(op.value)) {
      return { value: op.value, label: op.label, valueInputType: "none" };
    }
    if (TIMESTAMP_COMPARISON_OPERATORS.has(op.value)) {
      return { value: op.value, label: op.label, valueInputType: "date" };
    }
    if (MULTI_VALUE_STRING_OPERATORS.has(op.value)) {
      return {
        value: op.value,
        label: op.label,
        valueInputType: hasOptions ? "multi-select" : "text",
        ...(hasOptions && { valueOptions }),
      };
    }
    if (op.value === "equals" || op.value === "not_equals") {
      return {
        value: op.value,
        label: op.label,
        valueInputType: hasOptions ? "single-select" : "date",
        ...(hasOptions && { valueOptions }),
      };
    }
    return { value: op.value, label: op.label, valueInputType: "text" };
  });
}

/** String operators for fields with dataType "String". */
export function getStringOperators(
  valueOptions?: QueryBuilderOption[],
): OperatorDefinition[] {
  return mapStringLikeCatalogToDefinitions(STRING_OPERATORS, valueOptions);
}

/**
 * Wires `equals` / `not_equals` to single-select when a STRING field has dropdown options.
 *
 * @param operators - Base list from getStringOperators
 * @param valueOptions - Options for equals / not_equals selects
 * @returns Operators with select inputs when valueOptions are provided
 */
export function withSelectValuesOnStringEqualsOperators(
  operators: OperatorDefinition[],
  valueOptions: QueryBuilderOption[] | undefined,
): OperatorDefinition[] {
  if (!valueOptions?.length) return operators;

  return operators.map((op) => {
    if (op.value === "equals" || op.value === "not_equals") {
      return {
        ...op,
        valueInputType: "single-select",
        valueOptions,
      };
    }
    return op;
  });
}

/** Numeric operators for fields with dataType "Integer" or "Float". */
export function getNumericOperators(): OperatorDefinition[] {
  return NUMERIC_OPERATORS.map((op) => ({
    value: op.value,
    label: op.label,
    valueInputType: EMPTY_VALUE_OPERATORS.has(op.value) ? "none" : "number",
  }));
}

/** Boolean operators for fields with dataType "Boolean". */
export function getBooleanOperators(): OperatorDefinition[] {
  return BOOLEAN_OPERATORS.map((op) => ({
    value: op.value,
    label: op.label,
    valueInputType: "single-select",
    valueOptions: BOOLEAN_VALUE_OPTIONS,
  }));
}

/** Comparison and text operators for TIMESTAMP / DATE columns. */
export function getTimestampOperators(
  valueOptions?: QueryBuilderOption[],
): OperatorDefinition[] {
  return mapTimestampCatalogToDefinitions(valueOptions);
}

/** Operators for columns with an unrecognized or missing data type. */
export function getUnknownDatatypeOperators(
  valueOptions?: QueryBuilderOption[],
): OperatorDefinition[] {
  return mapStringLikeCatalogToDefinitions(
    UNKNOWN_DATATYPE_OPERATORS,
    valueOptions,
  );
}
